import { UseQueryResult } from '@tanstack/react-query';
import { UseFormReturn } from 'react-hook-form';

import { CertificateSummary, MCCSummaryItem } from '@/types';
import { resetForm } from '@/utils/FormHelper';
import { ReasonForInspectionOption } from '../moduleAFormOptionsData';
import { Form01Context, ModuleAForm01Values } from '../ModuleAFormSchemas';

/** State of Form01 */
export enum Form01State {
	State1_1,
	State1_3_2,
	InvalidCaseType,
	State2_1,
	State2_2,
	State2_2_1,
	State2_2_2,
	State3_1,
	State3_2,
	State6_2_1,
	State6_2_2,
	State7_1_2,
	State7_2,
}

/**
 * Hook to determine form01 states based on form context and values
 */
export const useForm01States = ({
	isBranchingActive,
	form01State,
	form01Context,
	form01,
	reasonForInspection,
}: {
	isBranchingActive: boolean | undefined;
	form01State: Form01State;
	form01Context: Form01Context;
	form01: UseFormReturn<ModuleAForm01Values>;
	reasonForInspection: ReasonForInspectionOption;
}) => {
	const showForm01NewOrReplacementMeter = !isBranchingActive || (form01State != Form01State.State1_1 && form01Context.caseType === MCCSummaryItem.caseType.METER_VALIDATION); // prettier-ignore
	const showForm01MCCNotFound = !isBranchingActive || form01State == Form01State.State1_3_2;
	const showForm01MCCTypeInvalid = !isBranchingActive || form01State == Form01State.InvalidCaseType;
	const showForm01ClientName = !isBranchingActive || (form01State >= Form01State.State2_1 && !showForm01MCCTypeInvalid); // prettier-ignore
	const showForm01WrongClientName = !isBranchingActive || form01State == Form01State.State2_2_2;
	const showForm01FormOwnedByDifferent =
		!isBranchingActive || form01State == Form01State.State7_1_2;
	const showForm01FormStarted = !isBranchingActive || form01State == Form01State.State7_2;
	const showForm01HasOutcomeForNewMeter = form01State == Form01State.State6_2_1;
	const showForm01HasOutcomeForExistingMeter =
		!isBranchingActive || form01State == Form01State.State6_2_2;
	const showForm01ClientNameCorrect = showForm01ClientName && !(showForm01FormOwnedByDifferent || showForm01HasOutcomeForNewMeter || showForm01HasOutcomeForExistingMeter); // prettier-ignore
	const showForm01Field5 =
		!isBranchingActive ||
		(form01State == Form01State.State3_2 &&
			form01Context.caseType === MCCSummaryItem.caseType.NEW_METER);
	const showForm01Field6 =
		!isBranchingActive || (showForm01Field5 && form01.watch('field5') === true);
	const hideForm01SubmissionButtons = [Form01State.State6_2_1, Form01State.State6_2_2, Form01State.State7_1_2, Form01State.State7_2].includes(form01State); // prettier-ignore
	const disableMeterCaseCode = form01.formState.disabled;
	const disableForm1SubmissionButton = [
		Form01State.InvalidCaseType,
		Form01State.State1_3_2,
		Form01State.State2_2_2,
		Form01State.State6_2_1,
		Form01State.State6_2_2,
		Form01State.State7_1_2,
		Form01State.State7_2,
	].includes(form01State);
	const isForm01ForMultiple = form01.watch('field5') === true;

	return {
		showForm01NewOrReplacementMeter,
		showForm01MCCNotFound,
		showForm01MCCTypeInvalid,
		showForm01ClientName,
		showForm01ClientNameCorrect,
		showForm01WrongClientName,
		showForm01FormOwnedByDifferent,
		showForm01FormStarted,
		showForm01HasOutcomeForNewMeter,
		showForm01HasOutcomeForExistingMeter,
		showForm01Field5,
		showForm01Field6,
		hideForm01SubmissionButtons,
		disableMeterCaseCode,
		disableForm1SubmissionButton,
		isForm01ForMultiple,
	};
};

/** Helpers for function determineNextForm01State */
const isValidCaseType = (caseType: MCCSummaryItem.caseType | undefined): boolean => {
	return caseType ? Object.values(MCCSummaryItem.caseType).includes(caseType) : false;
};

const determineStateForValidCase = (
	form01Context: Form01Context,
	reasonForInspection: ReasonForInspectionOption | undefined,
	mccsummaryData: MCCSummaryItem,
	msalClaimsContactId: string,
): Form01State => {
	if (form01Context.hasOutcome) {
		return determineStateWithOutcome(reasonForInspection);
	}

	if (form01Context.hasLinkedForm) {
		return determineStateWithLinkedForm(mccsummaryData, msalClaimsContactId);
	}

	return Form01State.State3_2;
};

const determineStateWithOutcome = (
	reasonForInspection: ReasonForInspectionOption | undefined,
): Form01State => {
	if (reasonForInspection === ReasonForInspectionOption.NewMeter) {
		return Form01State.State6_2_1;
	}
	if (reasonForInspection === ReasonForInspectionOption.ExistingMeter) {
		return Form01State.State6_2_2;
	}
	return Form01State.State2_1;
};

const determineStateWithLinkedForm = (
	mccsummaryData: MCCSummaryItem,
	msalClaimsContactId: string,
): Form01State => {
	if (mccsummaryData.linkedForm?.creator === msalClaimsContactId) {
		return Form01State.State7_2;
	}
	return Form01State.State7_1_2;
};
/** End Helpers for function determineNextForm01State */

/**
 * Determines the next form01 state using field values and MCC summary data
 */
export const determineNextForm01State = (
	isMeterCaseCodeValid: boolean | undefined,
	getMCCSummary: UseQueryResult<MCCSummaryItem, unknown>,
	form01Context: Form01Context,
	reasonForInspection: ReasonForInspectionOption | undefined,
	isCorrectClient: boolean | undefined,
	isFormDisabled: boolean,
	msalClaimsContactId: string,
): Form01State => {
	if (isFormDisabled || !isMeterCaseCodeValid || getMCCSummary.isError || getMCCSummary.isLoading) {
		return Form01State.State1_1;
	}

	if (!getMCCSummary.data) {
		return Form01State.State1_3_2;
	}

	if (!isValidCaseType(form01Context.caseType)) {
		return Form01State.InvalidCaseType;
	}

	if (isCorrectClient === undefined) {
		return Form01State.State2_1;
	}

	if (isCorrectClient === false) {
		return Form01State.State2_2_2;
	}

	return determineStateForValidCase(
		form01Context,
		reasonForInspection,
		getMCCSummary.data,
		msalClaimsContactId,
	);
};

/**
 * Restarting form 01 with mcc needs a full reset to avoid conflicts
 */
export const resetForm01AndSetMCC = (
	form01: UseFormReturn<ModuleAForm01Values>,
	meterCaseCode: string,
) => {
	resetForm(form01);
	form01.setValue('field1', meterCaseCode ?? '');
};

/**
 * Resets form01 fields and context except for field1 (meter case code)
 */
export const resetForm01AndContextExceptMCC = (
	form01: UseFormReturn<ModuleAForm01Values>,
	setForm01Context: React.Dispatch<React.SetStateAction<Form01Context>>,
) => {
	form01.resetField('field2');
	form01.resetField('field3');
	form01.resetField('field4');
	form01.resetField('field5');
	form01.resetField('field6');
	setForm01Context(prevContext => ({
		...prevContext,
		hasOutcome: false,
		hasLinkedForm: false,
		caseType: undefined,
	}));
};

/**
 * Returns the id of the created certificate and resets form01 to proceed if needed
 */
export const updateForm01ToProceedAfterCreate = (
	form01: UseFormReturn<ModuleAForm01Values>,
	form01Context: Form01Context,
	data: CertificateSummary,
) => {
	let createdCertificateId;

	const hasGeneratedSingleNewMC = (data.newCreatedCertificates ?? []).length === 1;
	const hasOutcomeOnMeterCase = form01Context.hasOutcome;
	const isForNewMeter = form01.getValues().field2 === ReasonForInspectionOption.NewMeter;
	const needsGenerateSingleNewMC = hasOutcomeOnMeterCase && isForNewMeter;

	const needsToOverwriteToProceed = hasGeneratedSingleNewMC && needsGenerateSingleNewMC;
	const generatedMC = (data.newCreatedCertificates ?? [])[0];

	if (needsToOverwriteToProceed && generatedMC) {
		const generatedMCC = generatedMC.meterCaseCode;
		form01.reset({ ...form01.getValues(), field1: generatedMCC }, { keepIsSubmitSuccessful: true }); // prettier-ignore
		createdCertificateId = generatedMC.id;
	} else {
		createdCertificateId = data.id;
	}

	return createdCertificateId;
};
